import { useUser } from "../context/UserContext";
import { useLocation } from "react-router-dom";
import { useCallback, useEffect, useRef } from "react";
import { logAuditTrace } from "../services/auditNavigationTraceService";
import { LABELNAMES } from "../constants/messages";
import { getModuleName } from "../utils/commonHelper";
import {
  GLOBALSETTINGS,
  ModuleNames,
  POSTIF_SIGN_ROUTE_PREFIX,
  POSTIF_SIGN_TRACE_LABEL,
} from "../data/constants";
import { getCurrentESTTime } from "../utils/dateHelper";

const useAuditNavigationTrace = () => {
  const { userId } = useUser();
  const location = useLocation();
  const pathVisitMapRef = useRef<Map<string, number>>(new Map());

  const trackNavigation = useCallback(() => {
    if (userId) {
      const now = Date.now();
      const currentPath = location.pathname;
      const lastVisited = pathVisitMapRef.current.get(currentPath);

      const pathSegments = location.pathname
        .split("/")
        .filter((segment) => segment && isNaN(Number(segment))); // Remove empty & numeric segments

      const lastSegment = pathSegments.pop() || LABELNAMES.FILE_EXPLORER;
      const normalizeDetailPage = GLOBALSETTINGS.LIST_DETAIL_PAGE.replace(/^\/+/, "") ?? "";
      if (lastSegment === normalizeDetailPage) {
        return;
      }

      let formattedName = "";

      var moduleName = getModuleName();
      if (moduleName === ModuleNames.FileExplorer) {
        formattedName = LABELNAMES.FILE_EXPLORER;
      } else if (location.pathname.toLowerCase().startsWith(POSTIF_SIGN_ROUTE_PREFIX)) {
        // The approver deep link's last segment is an opaque one-time token, and the generic
        // branch below names the page after the last segment. Without this the audit trail
        // records "Navigated to <token>" — unreadable, and it persists a value that belongs
        // only in the emailed link (Epic 79824 AC5 keeps this URL free of anything else).
        formattedName = POSTIF_SIGN_TRACE_LABEL;
      } else {
        formattedName = lastSegment
          .replace(/[-_]/g, " ") // Replace dashes/underscores with spaces
          .replace(/\b\w/g, (char) => char.toUpperCase());
      }

      if (!lastVisited || now - lastVisited >= GLOBALSETTINGS.MAX_TIME_DIFF_MS) {
        logAuditTrace({
          userId: userId,
          objectType: "Page",
          objectId: null,
          objectName: `Navigated to ${formattedName}`,
          action: "Navigated",
          navigationDateTime: getCurrentESTTime(),
        });
      }
      pathVisitMapRef.current.set(currentPath, now);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key, userId]);

  useEffect(() => {
    trackNavigation();
  }, [trackNavigation]);
};

export default useAuditNavigationTrace;
